import { makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "@intrasync/stores";
import { 
  Materials, 
  MaterialInventory, 
  MaterialTransactions,
  MaterialVendors,
  MaterialReorderRules
} from "../../types/MaterialTypes";

class MaterialsViewStore {
  // Observables
  currentTab: string = "dashboard";
  selectedMaterialId: number | null = null;
  selectedLocationId: number | null = null;
  selectedVendorId: number | null = null;
  inventoryFilter: "all" | "available" | "quarantine" | "expired" | "damaged" = "all";
  transactionFilter: "all" | "receipt" | "issue" | "transfer" | "adjustment" | "return" | "scrap" = "all";
  dateRange: { start: Date | null; end: Date | null } = { start: null, end: null };
  searchQuery: string = "";
  isLoading: boolean = false;
  errorMessage: string | null = null;
  currentDialogData: any = {};

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
  }

  // Actions
  setCurrentTab = (tab: string) => {
    this.currentTab = tab;
  };

  setSelectedMaterial = (materialId: number | null) => {
    this.selectedMaterialId = materialId;
  };

  setSelectedLocation = (locationId: number | null) => {
    this.selectedLocationId = locationId;
  };

  setSelectedVendor = (vendorId: number | null) => {
    this.selectedVendorId = vendorId;
  };

  setInventoryFilter = (filter: typeof this.inventoryFilter) => {
    this.inventoryFilter = filter;
  };

  setTransactionFilter = (filter: typeof this.transactionFilter) => {
    this.transactionFilter = filter;
  };

  setDateRange = (start: Date | null, end: Date | null) => {
    this.dateRange = { start, end };
  };

  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };

  setErrorMessage = (message: string | null) => {
    this.errorMessage = message;
  };

  setDialogData = (data: any) => {
    this.currentDialogData = data;
  };

  updateDialogData = (updates: any) => {
    this.currentDialogData = { ...this.currentDialogData, ...updates };
  };

  clearDialogData = () => {
    this.currentDialogData = {};
  };

  // Async Actions
  loadDashboardData = async () => {
    this.isLoading = true;
    this.errorMessage = null;
    
    try {
      await Promise.all([
        (this.rootStore as any).materialsStore?.find({ query: { $limit: 50 } }),
        (this.rootStore as any).materialInventoryStore?.find({ query: { $limit: 50 } }),
        (this.rootStore as any).materialTransactionsStore?.find({ 
          query: { 
            $limit: 10
          } 
        }),
      ]);
    } catch (error) {
      runInAction(() => {
        this.errorMessage = "Failed to load dashboard data";
        console.error("Dashboard data load error:", error);
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  performInventoryAdjustment = async (adjustment: Partial<MaterialTransactions>) => {
    this.isLoading = true;
    this.errorMessage = null;
    
    try {
      await (this.rootStore as any).materialTransactionsStore?.create(adjustment);
      // Refresh inventory after adjustment
      await (this.rootStore as any).materialInventoryStore?.find({});
      return true;
    } catch (error) {
      runInAction(() => {
        this.errorMessage = "Failed to perform inventory adjustment";
        console.error("Inventory adjustment error:", error);
      });
      return false;
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  checkReorderPoints = async () => {
    this.isLoading = true;
    
    try {
      const reorderRules = await (this.rootStore as any).materialReorderRulesStore?.find({
        query: { active: true }
      });
      
      const inventory = await (this.rootStore as any).materialInventoryStore?.find({});
      
      // Check which materials need reordering
      const needsReorder: number[] = [];
      
      runInAction(() => {
        // Logic to check reorder points would go here
        // This would compare inventory levels with reorder rules
        this.isLoading = false;
      });
      
      return needsReorder;
    } catch (error) {
      runInAction(() => {
        this.errorMessage = "Failed to check reorder points";
        console.error("Reorder check error:", error);
        this.isLoading = false;
      });
      return [];
    }
  };

  // Computed Values
  get materials() {
    return (this.rootStore as any).materialsStore?.entities || [];
  }

  get filteredMaterials() {
    if (!this.searchQuery) return this.materials;
    
    const query = this.searchQuery.toLowerCase();
    return this.materials.filter((material: Materials) => 
      material.name.toLowerCase().includes(query) ||
      material.materialCode.toLowerCase().includes(query) ||
      material.category.toLowerCase().includes(query)
    );
  }

  get inventory() {
    return (this.rootStore as any).materialInventoryStore?.entities || [];
  }

  get inventoryWithDetails() {
    const inventory = this.inventory;
    const materials = this.materials;
    const locations = this.locations;
    
    return inventory.map((inv: any) => {
      const material = materials.find((m: any) => m.id === inv.materialId);
      const location = locations.find((l: any) => l.id === inv.locationId);
      
      return {
        ...inv,
        materialCode: material?.materialCode || '',
        materialName: material?.name || 'Unknown Material',
        locationName: location?.name || `Location ${inv.locationId}`,
        unit: material?.unit || ''
      };
    });
  }

  get filteredInventory() {
    let filtered = this.inventory;
    
    if (this.inventoryFilter !== "all") {
      filtered = filtered.filter((inv: MaterialInventory) => 
        inv.status === this.inventoryFilter
      );
    }
    
    if (this.selectedLocationId) {
      filtered = filtered.filter((inv: MaterialInventory) => 
        inv.locationId === this.selectedLocationId
      );
    }
    
    return filtered;
  }

  get transactions() {
    return (this.rootStore as any).materialTransactionsStore?.entities || [];
  }

  get transactionsWithDetails() {
    const transactions = this.transactions;
    const materials = this.materials;
    const locations = this.locations;
    const jobs = (this.rootStore as any).jobStore?.entities || [];
    const users = (this.rootStore as any).usersStore?.entities || [];
    
    return transactions.map((trans: any) => {
      const material = materials.find((m: any) => m.id === trans.materialId);
      const fromLocation = locations.find((l: any) => l.id === trans.fromLocationId);
      const toLocation = locations.find((l: any) => l.id === trans.toLocationId);
      const job = jobs.find((j: any) => j.id === trans.jobId);
      const user = users.find((u: any) => u.id === trans.performedBy);
      
      return {
        ...trans,
        materialName: material?.name || 'Unknown Material',
        materialCode: material?.materialCode || '',
        unit: material?.unit || '',
        fromLocationName: fromLocation?.name || (trans.fromLocationId ? `Location ${trans.fromLocationId}` : '-'),
        toLocationName: toLocation?.name || (trans.toLocationId ? `Location ${trans.toLocationId}` : '-'),
        jobName: job?.name || (trans.jobId ? `Job ${trans.jobId}` : '-'),
        performedByName: user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : (trans.performedBy ? 'User' : 'System')
      };
    });
  }

  get filteredTransactions() {
    let filtered = this.transactions;
    
    if (this.transactionFilter !== "all") {
      filtered = filtered.filter((trans: MaterialTransactions) => 
        trans.transactionType === this.transactionFilter
      );
    }
    
    if (this.dateRange.start && this.dateRange.end) {
      filtered = filtered.filter((trans: MaterialTransactions) => {
        const transDate = new Date(trans.transactionDate);
        return transDate >= this.dateRange.start! && transDate <= this.dateRange.end!;
      });
    }
    
    return filtered;
  }

  get vendors() {
    return (this.rootStore as any).materialVendorsStore?.entities || [];
  }

  get locations() {
    return (this.rootStore as any).materialLocationsStore?.entities || [];
  }

  get reorderRules() {
    return (this.rootStore as any).materialReorderRulesStore?.entities || [];
  }

  get activeReorderRules() {
    return this.reorderRules.filter((rule: MaterialReorderRules) => rule.active);
  }

  get qualityCerts() {
    return (this.rootStore as any).materialQualityCertsStore?.entities || [];
  }

  get recentTransactions() {
    // Get the 10 most recent transactions sorted by date
    const sorted = [...this.transactions].sort((a: any, b: any) => {
      const dateA = new Date(a.transactionDate || a.createdAt).getTime();
      const dateB = new Date(b.transactionDate || b.createdAt).getTime();
      return dateB - dateA; // Most recent first
    });
    return sorted.slice(0, 10);
  }

  get lowStockMaterials() {
    return this.materials.filter((material: any) => {
      const materialInventory = this.inventory.filter((inv: any) => inv.materialId === material.id);
      const totalQuantity = materialInventory.reduce(
        (sum: number, inv: any) => sum + (inv.availableQuantity || 0),
        0
      );
      const rule = this.reorderRules.find((r: any) => r.materialId === material.id);
      return rule && totalQuantity <= rule.reorderPoint;
    });
  }

  get totalInventoryValue() {
    return this.inventory.reduce((sum: number, inv: any) => {
      const material = this.materials.find((m: any) => m.id === inv.materialId);
      const cost = inv.cost || material?.cost || 0;
      return sum + (inv.availableQuantity * cost);
    }, 0);
  }

  get quarantinedInventory() {
    return this.inventory.filter((inv: any) => inv.status === 'quarantine');
  }

  get selectedMaterial() {
    if (!this.selectedMaterialId) return null;
    return this.materials.find((m: Materials) => m.id === this.selectedMaterialId);
  }

  get selectedMaterialInventory() {
    if (!this.selectedMaterialId) return [];
    return this.inventory.filter((inv: MaterialInventory) => 
      inv.materialId === this.selectedMaterialId
    );
  }

  get selectedMaterialTransactions() {
    if (!this.selectedMaterialId) return [];
    return this.transactions.filter((trans: MaterialTransactions) => 
      trans.materialId === this.selectedMaterialId
    );
  }

  get selectedMaterialVendors() {
    if (!this.selectedMaterialId) return [];
    return this.vendors.filter((vendor: MaterialVendors) => 
      vendor.materialId === this.selectedMaterialId
    );
  }

  get inventoryStats() {
    const stats = {
      totalMaterials: this.materials.length,
      totalLocations: this.locations.length,
      lowStockItems: 0,
      expiredItems: 0,
      quarantinedItems: 0,
      totalValue: 0,
      itemsBelowReorderPoint: 0
    };

    this.inventory.forEach((inv: any) => {
      if (inv.status === "expired") stats.expiredItems++;
      if (inv.status === "quarantine") stats.quarantinedItems++;
      
      // Calculate value using cost and available quantity
      if (inv.cost && inv.availableQuantity) {
        stats.totalValue += inv.cost * inv.availableQuantity;
      } else if (inv.cost && inv.quantity) {
        // Fallback to quantity if availableQuantity not present
        stats.totalValue += inv.cost * inv.quantity;
      }
    });

    // Check for low stock based on reorder rules
    this.activeReorderRules.forEach((rule: MaterialReorderRules) => {
      const invItems = this.inventory.filter((inv: MaterialInventory) => 
        inv.materialId === rule.materialId
      );
      const totalQuantity = invItems.reduce((sum: number, inv: any) => sum + inv.availableQuantity, 0);
      if (totalQuantity <= rule.reorderPoint) {
        stats.lowStockItems++;
        stats.itemsBelowReorderPoint++;
      }
    });

    return stats;
  }

}

export default MaterialsViewStore;